/**
 * @file useCheckBoxEx.js
 * @description フォーム用チェックボックスフィールドを簡単に生成するためのカスタムフック
 *
 * このフックは、Material-UIのコンポーネントを使用して、
 * スタイリッシュで機能的なフォームチェックボックスフィールドを提供します。
 * データベーススキーマのtinyint、boolean型カラムから自動生成されます。
 *
 * @example
 * // 基本的な使用例
 * const [form, formProps] = useFormEx();
 * const { checkbox_status, checkbox_is_deleted } = useCheckBoxEx(form, formProps);
 *
 * // コンポーネント内での使用
 * return (
 *   <form>
 *     {checkbox_status({ label: "ステータス" })}
 *     {checkbox_is_deleted({ label: "削除済み" })}
 *   </form>
 * );
 *
 * @author MetroJS Team
 * @version 1.0.0
 * @since 2024-12-19
 */

"use client";

import logjs from "@metrojs/logjs";
const log = new logjs("useCheckBoxEx");
const baseStyle = {}; // ベーススタイル
/**
 * チェックボックスフィールドを生成するためのカスタムフック
 * @param {Object} form - フォームデータオブジェクト
 * @param {Object} formProps - フォームプロパティオブジェクト
 * @returns {Object} 各種チェックボックスフィールド生成関数のオブジェクト
 */
const useCheckBoxEx = (form = {}, formProps = {}) => {
  const is_standard = true; // 標準のチェックボックスを使用するかどうか
  /**
   * 締め処理フラグ　1=部品、3=車輌チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 締め処理フラグ　1=部品、3=車輌チェックボックスのJSX要素
   */
  const checkbox_Wellset_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Wellset_flg form.Wellset_flg:${form?.Wellset_flg || ""} params:`, params);
    return formProps.checkbox("Wellset_flg", { label: "締め処理フラグ　1=部品、3=車輌", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * ダウンロードフラグ 1=済チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} ダウンロードフラグ 1=済チェックボックスのJSX要素
   */
  const checkbox_dl_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_dl_flg form.dl_flg:${form?.dl_flg || ""} params:`, params);
    return formProps.checkbox("dl_flg", { label: "ダウンロードフラグ 1=済", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 書類有無　1:有　9:無チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 書類有無　1:有　9:無チェックボックスのJSX要素
   */
  const checkbox_Shorui_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Shorui_flg form.Shorui_flg:${form?.Shorui_flg || ""} params:`, params);
    return formProps.checkbox("Shorui_flg", { label: "書類有無　1:有　9:無", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 書類送料 1:あり 0:なしチェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 書類送料 1:あり 0:なしチェックボックスのJSX要素
   */
  const checkbox_ShoruiSoryo_Flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_ShoruiSoryo_Flg form.ShoruiSoryo_Flg:${form?.ShoruiSoryo_Flg || ""} params:`, params);
    return formProps.checkbox("ShoruiSoryo_Flg", { label: "書類送料 1:あり 0:なし", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 書類送付書フラグ 1:あり 9:なしチェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 書類送付書フラグ 1:あり 9:なしチェックボックスのJSX要素
   */
  const checkbox_ShoruiSoufu_Flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_ShoruiSoufu_Flg form.ShoruiSoufu_Flg:${form?.ShoruiSoufu_Flg || ""} params:`, params);
    return formProps.checkbox("ShoruiSoufu_Flg", { label: "書類送付書フラグ 1:あり 9:なし", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * お問い合わせ種類　1:生産者 2:整備チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} お問い合わせ種類　1:生産者 2:整備チェックボックスのJSX要素
   */
  const checkbox_ContactKind = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_ContactKind form.ContactKind:${form?.ContactKind || ""} params:`, params);
    return formProps.checkbox("ContactKind", { label: "お問い合わせ種類　1:生産者 2:整備", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 販売購入フラグ 2:注文確定 5:発送済 8:値引 9:返品チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 販売購入フラグ 2:注文確定 5:発送済 8:値引 9:返品チェックボックスのJSX要素
   */
  const checkbox_Buysell_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Buysell_flg form.Buysell_flg:${form?.Buysell_flg || ""} params:`, params);
    return formProps.checkbox("Buysell_flg", { label: "販売購入フラグ 2:注文確定 5:発送済 8:値引 9:返品", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 手数料除外フラグ 1=除外するチェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 手数料除外フラグ 1=除外するチェックボックスのJSX要素
   */
  const checkbox_Tesuryo_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Tesuryo_flg form.Tesuryo_flg:${form?.Tesuryo_flg || ""} params:`, params);
    return formProps.checkbox("Tesuryo_flg", { label: "手数料除外フラグ 1=除外する", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌販売表示フラグ 1=非表示チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌販売表示フラグ 1=非表示チェックボックスのJSX要素
   */
  const checkbox_CarView_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_CarView_flg form.CarView_flg:${form?.CarView_flg || ""} params:`, params);
    return formProps.checkbox("CarView_flg", { label: "車輌販売表示フラグ 1=非表示", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：書類有無 1:有 5:どちらでも 9:無チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：書類有無 1:有 5:どちらでも 9:無チェックボックスのJSX要素
   */
  const checkbox_C_Shorui_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_Shorui_flg form.C_Shorui_flg:${form?.C_Shorui_flg || ""} params:`, params);
    return formProps.checkbox("C_Shorui_flg", { label: "車輌：書類有無 1:有 5:どちらでも 9:無", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 発注フラグ　0:入札済　2:成約　5:引渡済　8:値引　9:返品チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 発注フラグ　0:入札済　2:成約　5:引渡済　8:値引　9:返品チェックボックスのJSX要素
   */
  const checkbox_Hacchu_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Hacchu_flg form.Hacchu_flg:${form?.Hacchu_flg || ""} params:`, params);
    return formProps.checkbox("Hacchu_flg", { label: "発注フラグ　0:入札済　2:成約　5:引渡済　8:値引　9:返品", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：リサイクル有無 1:有 9:無チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：リサイクル有無 1:有 9:無チェックボックスのJSX要素
   */
  const checkbox_C_Recycle_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_Recycle_flg form.C_Recycle_flg:${form?.C_Recycle_flg || ""} params:`, params);
    return formProps.checkbox("C_Recycle_flg", { label: "車輌：リサイクル有無 1:有 9:無", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：車歴 1:自家用 2:レンタカーチェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：車歴 1:自家用 2:レンタカーチェックボックスのJSX要素
   */
  const checkbox_C_Shareki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_Shareki form.C_Shareki:${form?.C_Shareki || ""} params:`, params);
    return formProps.checkbox("C_Shareki", { label: "車輌：車歴 1:自家用 2:レンタカー", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：修復歴チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：修復歴チェックボックスのJSX要素
   */
  const checkbox_C_Shufuku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_Shufuku form.C_Shufuku:${form?.C_Shufuku || ""} params:`, params);
    return formProps.checkbox("C_Shufuku", { label: "車輌：修復歴", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　前部チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　前部チェックボックスのJSX要素
   */
  const checkbox_C_Sonshou01 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_Sonshou01 form.C_Sonshou01:${form?.C_Sonshou01 || ""} params:`, params);
    return formProps.checkbox("C_Sonshou01", { label: "車輌：損傷　前部", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　後部チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　後部チェックボックスのJSX要素
   */
  const checkbox_C_Sonshou02 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_Sonshou02 form.C_Sonshou02:${form?.C_Sonshou02 || ""} params:`, params);
    return formProps.checkbox("C_Sonshou02", { label: "車輌：損傷　後部", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　右側チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　右側チェックボックスのJSX要素
   */
  const checkbox_C_Sonshou03 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_Sonshou03 form.C_Sonshou03:${form?.C_Sonshou03 || ""} params:`, params);
    return formProps.checkbox("C_Sonshou03", { label: "車輌：損傷　右側", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　左側チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　左側チェックボックスのJSX要素
   */
  const checkbox_C_Sonshou04 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_Sonshou04 form.C_Sonshou04:${form?.C_Sonshou04 || ""} params:`, params);
    return formProps.checkbox("C_Sonshou04", { label: "車輌：損傷　左側", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　ルーフチェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　ルーフチェックボックスのJSX要素
   */
  const checkbox_C_Sonshou05 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_Sonshou05 form.C_Sonshou05:${form?.C_Sonshou05 || ""} params:`, params);
    return formProps.checkbox("C_Sonshou05", { label: "車輌：損傷　ルーフ", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　盗難チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　盗難チェックボックスのJSX要素
   */
  const checkbox_C_Sonshou06 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_Sonshou06 form.C_Sonshou06:${form?.C_Sonshou06 || ""} params:`, params);
    return formProps.checkbox("C_Sonshou06", { label: "車輌：損傷　盗難", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　水没チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　水没チェックボックスのJSX要素
   */
  const checkbox_C_Sonshou07 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_Sonshou07 form.C_Sonshou07:${form?.C_Sonshou07 || ""} params:`, params);
    return formProps.checkbox("C_Sonshou07", { label: "車輌：損傷　水没", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　炎上チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　炎上チェックボックスのJSX要素
   */
  const checkbox_C_Sonshou08 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_Sonshou08 form.C_Sonshou08:${form?.C_Sonshou08 || ""} params:`, params);
    return formProps.checkbox("C_Sonshou08", { label: "車輌：損傷　炎上", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　エアバッグ展開チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　エアバッグ展開チェックボックスのJSX要素
   */
  const checkbox_C_Sonshou09 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_Sonshou09 form.C_Sonshou09:${form?.C_Sonshou09 || ""} params:`, params);
    return formProps.checkbox("C_Sonshou09", { label: "車輌：損傷　エアバッグ展開", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　横転チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　横転チェックボックスのJSX要素
   */
  const checkbox_C_Sonshou10 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_Sonshou10 form.C_Sonshou10:${form?.C_Sonshou10 || ""} params:`, params);
    return formProps.checkbox("C_Sonshou10", { label: "車輌：損傷　横転", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：足回り 1:不明 5:OK 9:NGチェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：足回り 1:不明 5:OK 9:NGチェックボックスのJSX要素
   */
  const checkbox_C_Ashimawari = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_Ashimawari form.C_Ashimawari:${form?.C_Ashimawari || ""} params:`, params);
    return formProps.checkbox("C_Ashimawari", { label: "車輌：足回り 1:不明 5:OK 9:NG", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：足回りNGの場合 左前 1:有 9:無チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：足回りNGの場合 左前 1:有 9:無チェックボックスのJSX要素
   */
  const checkbox_C_AshimawariNG1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_AshimawariNG1 form.C_AshimawariNG1:${form?.C_AshimawariNG1 || ""} params:`, params);
    return formProps.checkbox("C_AshimawariNG1", { label: "車輌：足回りNGの場合 左前 1:有 9:無", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：足回りNGの場合 右前 1:有 9:無チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：足回りNGの場合 右前 1:有 9:無チェックボックスのJSX要素
   */
  const checkbox_C_AshimawariNG2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_AshimawariNG2 form.C_AshimawariNG2:${form?.C_AshimawariNG2 || ""} params:`, params);
    return formProps.checkbox("C_AshimawariNG2", { label: "車輌：足回りNGの場合 右前 1:有 9:無", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：足回りNGの場合 左後 1:有 9:無チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：足回りNGの場合 左後 1:有 9:無チェックボックスのJSX要素
   */
  const checkbox_C_AshimawariNG3 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_AshimawariNG3 form.C_AshimawariNG3:${form?.C_AshimawariNG3 || ""} params:`, params);
    return formProps.checkbox("C_AshimawariNG3", { label: "車輌：足回りNGの場合 左後 1:有 9:無", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：足回りNGの場合 右後 1:有 9:無チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：足回りNGの場合 右後 1:有 9:無チェックボックスのJSX要素
   */
  const checkbox_C_AshimawariNG4 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_AshimawariNG4 form.C_AshimawariNG4:${form?.C_AshimawariNG4 || ""} params:`, params);
    return formProps.checkbox("C_AshimawariNG4", { label: "車輌：足回りNGの場合 右後 1:有 9:無", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：車輌看板チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：車輌看板チェックボックスのJSX要素
   */
  const checkbox_C_SharyoKanban = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_C_SharyoKanban form.C_SharyoKanban:${form?.C_SharyoKanban || ""} params:`, params);
    return formProps.checkbox("C_SharyoKanban", { label: "車輌：車輌看板", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 価格の評価チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 価格の評価チェックボックスのJSX要素
   */
  const checkbox_Eval_Kakaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Eval_Kakaku form.Eval_Kakaku:${form?.Eval_Kakaku || ""} params:`, params);
    return formProps.checkbox("Eval_Kakaku", { label: "価格の評価", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 品質の評価チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 品質の評価チェックボックスのJSX要素
   */
  const checkbox_Eval_Hinshitsu = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Eval_Hinshitsu form.Eval_Hinshitsu:${form?.Eval_Hinshitsu || ""} params:`, params);
    return formProps.checkbox("Eval_Hinshitsu", { label: "品質の評価", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 梱包の評価チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 梱包の評価チェックボックスのJSX要素
   */
  const checkbox_Eval_Konpou = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Eval_Konpou form.Eval_Konpou:${form?.Eval_Konpou || ""} params:`, params);
    return formProps.checkbox("Eval_Konpou", { label: "梱包の評価", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 納期の評価チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 納期の評価チェックボックスのJSX要素
   */
  const checkbox_Eval_Nouki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Eval_Nouki form.Eval_Nouki:${form?.Eval_Nouki || ""} params:`, params);
    return formProps.checkbox("Eval_Nouki", { label: "納期の評価", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 手配種別　1=自社 2=他社チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 手配種別　1=自社 2=他社チェックボックスのJSX要素
   */
  const checkbox_PrepKind = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_PrepKind form.PrepKind:${form?.PrepKind || ""} params:`, params);
    return formProps.checkbox("PrepKind", { label: "手配種別　1=自社 2=他社", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * QAフラグ 1=質問 2=回答チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} QAフラグ 1=質問 2=回答チェックボックスのJSX要素
   */
  const checkbox_QA_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_QA_flg form.QA_flg:${form?.QA_flg || ""} params:`, params);
    return formProps.checkbox("QA_flg", { label: "QAフラグ 1=質問 2=回答", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 未読既読フラグ 1:既読チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 未読既読フラグ 1:既読チェックボックスのJSX要素
   */
  const checkbox_al_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_al_flg form.al_flg:${form?.al_flg || ""} params:`, params);
    return formProps.checkbox("al_flg", { label: "未読既読フラグ 1:既読", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 未読既読フラグ 1=既読チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 未読既読フラグ 1=既読チェックボックスのJSX要素
   */
  const checkbox_al_flg2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_al_flg2 form.al_flg2:${form?.al_flg2 || ""} params:`, params);
    return formProps.checkbox("al_flg2", { label: "未読既読フラグ 1=既読", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 一覧表示フラグ NULL:表示　9:非表示チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 一覧表示フラグ NULL:表示　9:非表示チェックボックスのJSX要素
   */
  const checkbox_view_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_view_flg form.view_flg:${form?.view_flg || ""} params:`, params);
    return formProps.checkbox("view_flg", { label: "一覧表示フラグ NULL:表示　9:非表示", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 再見積フラグ　1:再見積済チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 再見積フラグ　1:再見積済チェックボックスのJSX要素
   */
  const checkbox_Sai_MitsumoriFlg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Sai_MitsumoriFlg form.Sai_MitsumoriFlg:${form?.Sai_MitsumoriFlg || ""} params:`, params);
    return formProps.checkbox("Sai_MitsumoriFlg", { label: "再見積フラグ　1:再見積済", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * CO2集計 1=対象チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} CO2集計 1=対象チェックボックスのJSX要素
   */
  const checkbox_co2_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_co2_flg form.co2_flg:${form?.co2_flg || ""} params:`, params);
    return formProps.checkbox("co2_flg", { label: "CO2集計 1=対象", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * CO2集計リビルト 1=対象チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} CO2集計リビルト 1=対象チェックボックスのJSX要素
   */
  const checkbox_co2_flg_r = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_co2_flg_r form.co2_flg_r:${form?.co2_flg_r || ""} params:`, params);
    return formProps.checkbox("co2_flg_r", { label: "CO2集計リビルト 1=対象", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 走行距離フラグ　1:走行距離　2:不明　3:交換　4:改ざんチェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 走行距離フラグ　1:走行距離　2:不明　3:交換　4:改ざんチェックボックスのJSX要素
   */
  const checkbox_Soukou_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Soukou_flg form.Soukou_flg:${form?.Soukou_flg || ""} params:`, params);
    return formProps.checkbox("Soukou_flg", { label: "走行距離フラグ　1:走行距離　2:不明　3:交換　4:改ざん", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：前部チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：前部チェックボックスのJSX要素
   */
  const checkbox_Sonshou01 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Sonshou01 form.Sonshou01:${form?.Sonshou01 || ""} params:`, params);
    return formProps.checkbox("Sonshou01", { label: "損傷箇所：前部", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：後部チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：後部チェックボックスのJSX要素
   */
  const checkbox_Sonshou02 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Sonshou02 form.Sonshou02:${form?.Sonshou02 || ""} params:`, params);
    return formProps.checkbox("Sonshou02", { label: "損傷箇所：後部", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：右側チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：右側チェックボックスのJSX要素
   */
  const checkbox_Sonshou03 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Sonshou03 form.Sonshou03:${form?.Sonshou03 || ""} params:`, params);
    return formProps.checkbox("Sonshou03", { label: "損傷箇所：右側", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：左側チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：左側チェックボックスのJSX要素
   */
  const checkbox_Sonshou04 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Sonshou04 form.Sonshou04:${form?.Sonshou04 || ""} params:`, params);
    return formProps.checkbox("Sonshou04", { label: "損傷箇所：左側", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：ルーフチェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：ルーフチェックボックスのJSX要素
   */
  const checkbox_Sonshou05 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Sonshou05 form.Sonshou05:${form?.Sonshou05 || ""} params:`, params);
    return formProps.checkbox("Sonshou05", { label: "損傷箇所：ルーフ", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：盗難チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：盗難チェックボックスのJSX要素
   */
  const checkbox_Sonshou06 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Sonshou06 form.Sonshou06:${form?.Sonshou06 || ""} params:`, params);
    return formProps.checkbox("Sonshou06", { label: "損傷箇所：盗難", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：水没チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：水没チェックボックスのJSX要素
   */
  const checkbox_Sonshou07 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Sonshou07 form.Sonshou07:${form?.Sonshou07 || ""} params:`, params);
    return formProps.checkbox("Sonshou07", { label: "損傷箇所：水没", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：炎上チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：炎上チェックボックスのJSX要素
   */
  const checkbox_Sonshou08 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Sonshou08 form.Sonshou08:${form?.Sonshou08 || ""} params:`, params);
    return formProps.checkbox("Sonshou08", { label: "損傷箇所：炎上", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：エアバッグ展開チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：エアバッグ展開チェックボックスのJSX要素
   */
  const checkbox_Sonshou09 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Sonshou09 form.Sonshou09:${form?.Sonshou09 || ""} params:`, params);
    return formProps.checkbox("Sonshou09", { label: "損傷箇所：エアバッグ展開", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：横転チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：横転チェックボックスのJSX要素
   */
  const checkbox_Sonshou10 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Sonshou10 form.Sonshou10:${form?.Sonshou10 || ""} params:`, params);
    return formProps.checkbox("Sonshou10", { label: "損傷箇所：横転", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：下回りチェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：下回りチェックボックスのJSX要素
   */
  const checkbox_Sonshou11 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Sonshou11 form.Sonshou11:${form?.Sonshou11 || ""} params:`, params);
    return formProps.checkbox("Sonshou11", { label: "損傷箇所：下回り", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：詳細不明チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：詳細不明チェックボックスのJSX要素
   */
  const checkbox_Sonshou12 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Sonshou12 form.Sonshou12:${form?.Sonshou12 || ""} params:`, params);
    return formProps.checkbox("Sonshou12", { label: "損傷箇所：詳細不明", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 足回り　1:不明　2:OK　9:NGチェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 足回り　1:不明　2:OK　9:NGチェックボックスのJSX要素
   */
  const checkbox_Ashimawari = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_Ashimawari form.Ashimawari:${form?.Ashimawari || ""} params:`, params);
    return formProps.checkbox("Ashimawari", { label: "足回り　1:不明　2:OK　9:NG", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 足回りNG：左前チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 足回りNG：左前チェックボックスのJSX要素
   */
  const checkbox_AshimawariNG1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_AshimawariNG1 form.AshimawariNG1:${form?.AshimawariNG1 || ""} params:`, params);
    return formProps.checkbox("AshimawariNG1", { label: "足回りNG：左前", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 足回りNG：右前チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 足回りNG：右前チェックボックスのJSX要素
   */
  const checkbox_AshimawariNG2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_AshimawariNG2 form.AshimawariNG2:${form?.AshimawariNG2 || ""} params:`, params);
    return formProps.checkbox("AshimawariNG2", { label: "足回りNG：右前", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 足回りNG：左後チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 足回りNG：左後チェックボックスのJSX要素
   */
  const checkbox_AshimawariNG3 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_AshimawariNG3 form.AshimawariNG3:${form?.AshimawariNG3 || ""} params:`, params);
    return formProps.checkbox("AshimawariNG3", { label: "足回りNG：左後", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 足回りNG：右後チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 足回りNG：右後チェックボックスのJSX要素
   */
  const checkbox_AshimawariNG4 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_AshimawariNG4 form.AshimawariNG4:${form?.AshimawariNG4 || ""} params:`, params);
    return formProps.checkbox("AshimawariNG4", { label: "足回りNG：右後", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 閲覧フラグ　1=見積　2=もぎとりチェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 閲覧フラグ　1=見積　2=もぎとりチェックボックスのJSX要素
   */
  const checkbox_ViewFlg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_ViewFlg form.ViewFlg:${form?.ViewFlg || ""} params:`, params);
    return formProps.checkbox("ViewFlg", { label: "閲覧フラグ　1=見積　2=もぎとり", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * マッチング対象：外装フロント　1=有効　0=無効チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：外装フロント　1=有効　0=無効チェックボックスのJSX要素
   */
  const checkbox_CBunruiCD100 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_CBunruiCD100 form.CBunruiCD100:${form?.CBunruiCD100 || ""} params:`, params);
    return formProps.checkbox("CBunruiCD100", { label: "マッチング対象：外装フロント　1=有効　0=無効", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * マッチング対象：外装右側面　1=有効　0=無効チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：外装右側面　1=有効　0=無効チェックボックスのJSX要素
   */
  const checkbox_CBunruiCD110 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_CBunruiCD110 form.CBunruiCD110:${form?.CBunruiCD110 || ""} params:`, params);
    return formProps.checkbox("CBunruiCD110", { label: "マッチング対象：外装右側面　1=有効　0=無効", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * マッチング対象：外装左側面　1=有効　0=無効チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：外装左側面　1=有効　0=無効チェックボックスのJSX要素
   */
  const checkbox_CBunruiCD120 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_CBunruiCD120 form.CBunruiCD120:${form?.CBunruiCD120 || ""} params:`, params);
    return formProps.checkbox("CBunruiCD120", { label: "マッチング対象：外装左側面　1=有効　0=無効", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * マッチング対象：外装リア　1=有効　0=無効チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：外装リア　1=有効　0=無効チェックボックスのJSX要素
   */
  const checkbox_CBunruiCD140 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_CBunruiCD140 form.CBunruiCD140:${form?.CBunruiCD140 || ""} params:`, params);
    return formProps.checkbox("CBunruiCD140", { label: "マッチング対象：外装リア　1=有効　0=無効", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * マッチング対象：エンジン　1=有効　0=無効チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：エンジン　1=有効　0=無効チェックボックスのJSX要素
   */
  const checkbox_CBunruiCD2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_CBunruiCD2 form.CBunruiCD2:${form?.CBunruiCD2 || ""} params:`, params);
    return formProps.checkbox("CBunruiCD2", { label: "マッチング対象：エンジン　1=有効　0=無効", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * マッチング対象：ミッション　1=有効　0=無効チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：ミッション　1=有効　0=無効チェックボックスのJSX要素
   */
  const checkbox_CBunruiCD3 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_CBunruiCD3 form.CBunruiCD3:${form?.CBunruiCD3 || ""} params:`, params);
    return formProps.checkbox("CBunruiCD3", { label: "マッチング対象：ミッション　1=有効　0=無効", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * マッチング対象：走行　1=有効　0=無効チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：走行　1=有効　0=無効チェックボックスのJSX要素
   */
  const checkbox_CBunruiCD4 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_CBunruiCD4 form.CBunruiCD4:${form?.CBunruiCD4 || ""} params:`, params);
    return formProps.checkbox("CBunruiCD4", { label: "マッチング対象：走行　1=有効　0=無効", is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * ログイン後の初期表示ページ　1=部品　2=車輌チェックボックス
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} ログイン後の初期表示ページ　1=部品　2=車輌チェックボックスのJSX要素
   */
  const checkbox_DefaultPage = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`checkbox_DefaultPage form.DefaultPage:${form?.DefaultPage || ""} params:`, params);
    return formProps.checkbox("DefaultPage", { label: "ログイン後の初期表示ページ　1=部品　2=車輌", is_standard, style: baseStyle, ...params }, is_debug);
  };

  return {
    checkbox_Wellset_flg,
    checkbox_dl_flg,
    checkbox_Shorui_flg,
    checkbox_ShoruiSoryo_Flg,
    checkbox_ShoruiSoufu_Flg,
    checkbox_ContactKind,
    checkbox_Buysell_flg,
    checkbox_Tesuryo_flg,
    checkbox_CarView_flg,
    checkbox_C_Shorui_flg,
    checkbox_Hacchu_flg,
    checkbox_C_Recycle_flg,
    checkbox_C_Shareki,
    checkbox_C_Shufuku,
    checkbox_C_Sonshou01,
    checkbox_C_Sonshou02,
    checkbox_C_Sonshou03,
    checkbox_C_Sonshou04,
    checkbox_C_Sonshou05,
    checkbox_C_Sonshou06,
    checkbox_C_Sonshou07,
    checkbox_C_Sonshou08,
    checkbox_C_Sonshou09,
    checkbox_C_Sonshou10,
    checkbox_C_Ashimawari,
    checkbox_C_AshimawariNG1,
    checkbox_C_AshimawariNG2,
    checkbox_C_AshimawariNG3,
    checkbox_C_AshimawariNG4,
    checkbox_C_SharyoKanban,
    checkbox_Eval_Kakaku,
    checkbox_Eval_Hinshitsu,
    checkbox_Eval_Konpou,
    checkbox_Eval_Nouki,
    checkbox_PrepKind,
    checkbox_QA_flg,
    checkbox_al_flg,
    checkbox_al_flg2,
    checkbox_view_flg,
    checkbox_Sai_MitsumoriFlg,
    checkbox_co2_flg,
    checkbox_co2_flg_r,
    checkbox_Soukou_flg,
    checkbox_Sonshou01,
    checkbox_Sonshou02,
    checkbox_Sonshou03,
    checkbox_Sonshou04,
    checkbox_Sonshou05,
    checkbox_Sonshou06,
    checkbox_Sonshou07,
    checkbox_Sonshou08,
    checkbox_Sonshou09,
    checkbox_Sonshou10,
    checkbox_Sonshou11,
    checkbox_Sonshou12,
    checkbox_Ashimawari,
    checkbox_AshimawariNG1,
    checkbox_AshimawariNG2,
    checkbox_AshimawariNG3,
    checkbox_AshimawariNG4,
    checkbox_ViewFlg,
    checkbox_CBunruiCD100,
    checkbox_CBunruiCD110,
    checkbox_CBunruiCD120,
    checkbox_CBunruiCD140,
    checkbox_CBunruiCD2,
    checkbox_CBunruiCD3,
    checkbox_CBunruiCD4,
    checkbox_DefaultPage,
  };
};

export default useCheckBoxEx;
